import React from 'react';

export default function AiGeneratePromptModal({
  isOpen,
  onClose,
  onConfirmAiGenerate,
  onConfirmBlankForm,
  targetPersonas = [],
  importedLeads = [],
  importedLeadRoles = [],
  matrix = [],
  isRoleMismatch = false,
}) {
  if (!isOpen) return null;

  const hasImportedLeads = importedLeads && importedLeads.length > 0;
  const rolesToTarget = hasImportedLeads
    ? importedLeadRoles.map((r) => r.role)
    : targetPersonas.length > 0
      ? targetPersonas
      : ['Executive Decision Maker'];

  const currentMatrixRoles = (matrix || []).map((m) => (m.persona || '').trim()).filter(Boolean);

  const matrixLowerSet = new Set(currentMatrixRoles.map((r) => r.toLowerCase()));
  const tab2RolesLowerSet = new Set(rolesToTarget.map((r) => r.toLowerCase()));

  const updatedLeadRoles = hasImportedLeads
    ? importedLeadRoles.filter((item) => !matrixLowerSet.has(item.role.toLowerCase()))
    : [];

  return (
    <div className="cmp-modal-backdrop" onClick={onClose}>
      <div
        className="cmp-modal-container"
        style={{ maxWidth: '620px' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="cmp-modal-header" style={{ padding: '16px 20px' }}>
          <div className="cmp-modal-header-left">
            <div
              className="cmp-modal-icon-badge"
              style={{
                background: isRoleMismatch ? '#eff6ff' : '#f0fdf4',
                color: isRoleMismatch ? '#2563eb' : '#16a34a',
                border: `1px solid ${isRoleMismatch ? '#bfdbfe' : '#bbf7d0'}`,
              }}
            >
              <i className={isRoleMismatch ? 'fas fa-sync-alt' : 'fas fa-magic'} />
            </div>
            <div>
              <h3 className="cmp-modal-title" style={{ fontSize: '15px', color: '#0f172a' }}>
                {isRoleMismatch
                  ? 'Sync Strategy with Updated Lead Roles?'
                  : 'Generate Strategy for Tab 3, 4 & 5?'}
              </h3>
              <p className="cmp-modal-subtitle" style={{ fontSize: '12px', color: '#64748b' }}>
                {isRoleMismatch
                  ? 'Decision-maker roles in Tab 2 (synced with CRM) differ from your current Tab 3 Messaging Matrix.'
                  : 'Synthesize tailored messaging, cadence touchpoints, and KPI forecasts.'}
              </p>
            </div>
          </div>
          <button
            type="button"
            className="cmp-modal-close-btn"
            onClick={onClose}
            title="Close modal"
          >
            <i className="fas fa-times" />
          </button>
        </div>

        {/* Modal Body */}
        <div
          className="cmp-modal-body"
          style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '12px' }}
        >
          {isRoleMismatch ? (
            <>
              {/* Updated Lead Highlight Strip */}
              {updatedLeadRoles.length > 0 && (
                <div
                  style={{
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    padding: '10px 12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    fontSize: '12.5px',
                  }}
                >
                  <div
                    style={{
                      width: '22px',
                      height: '22px',
                      borderRadius: '50%',
                      background: '#dbeafe',
                      color: '#1d4ed8',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '10px',
                      flexShrink: 0,
                    }}
                  >
                    <i className="fas fa-user-edit" />
                  </div>
                  <div style={{ flex: 1, color: '#334155', lineHeight: '1.4' }}>
                    <span style={{ fontWeight: 600, color: '#0f172a' }}>
                      Updated CRM Lead
                      {updatedLeadRoles.flatMap((r) => r.leads).length > 1 ? 's' : ''}:{' '}
                    </span>
                    {updatedLeadRoles.map((item, idx) => {
                      const leadNames = item.leads
                        .map((l) => {
                          const name =
                            l.name ||
                            `${l.firstName || ''} ${l.lastName || ''}`.trim() ||
                            l.email ||
                            'Lead';
                          const company = l.company ? ` (${l.company})` : '';
                          return `${name}${company}`;
                        })
                        .filter(Boolean)
                        .join(', ');
                      return (
                        <span key={idx}>
                          <strong>{leadNames}</strong> &rarr; Role:{' '}
                          <span style={{ fontWeight: 600, color: '#2563eb' }}>{item.role}</span>
                          {idx < updatedLeadRoles.length - 1 ? '; ' : ''}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Side-by-Side Comparison Container */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '10px',
                }}
              >
                {/* Column 1: Tab 2 Synced Leads */}
                <div
                  style={{
                    background: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    padding: '10px 12px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      paddingBottom: '5px',
                      borderBottom: '1px solid #f1f5f9',
                    }}
                  >
                    <span
                      style={{
                        fontSize: '11px',
                        fontWeight: 700,
                        color: '#0f172a',
                        textTransform: 'uppercase',
                        letterSpacing: '0.4px',
                      }}
                    >
                      <i
                        className="fas fa-id-badge"
                        style={{ marginRight: '5px', color: '#2563eb' }}
                      />
                      Tab 2: {hasImportedLeads ? 'Leads & Roles' : 'Personas'}
                    </span>
                    <span style={{ fontSize: '11px', color: '#64748b' }}>
                      {rolesToTarget.length} role{rolesToTarget.length > 1 ? 's' : ''}
                    </span>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    {hasImportedLeads && importedLeadRoles.length > 0 ? (
                      importedLeadRoles.map((item, idx) => {
                        const isNewOrUpdated = !matrixLowerSet.has(item.role.toLowerCase());
                        const leadNames = item.leads.map((l) => {
                          const name =
                            l.name ||
                            `${l.firstName || ''} ${l.lastName || ''}`.trim() ||
                            l.email ||
                            'Lead';
                          const companyStr = l.company ? ` (${l.company})` : '';
                          return `${name}${companyStr}`;
                        });
                        const leadSummary =
                          leadNames.length <= 2
                            ? leadNames.join(', ')
                            : `${leadNames.slice(0, 2).join(', ')} +${leadNames.length - 2} more`;

                        return (
                          <div
                            key={idx}
                            style={{
                              background: isNewOrUpdated ? '#f0fdf4' : '#f8fafc',
                              border: `1px solid ${isNewOrUpdated ? '#bbf7d0' : '#e2e8f0'}`,
                              borderRadius: '6px',
                              padding: '6px 8px',
                              fontSize: '11.5px',
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: '4px',
                              }}
                            >
                              <span style={{ fontWeight: 600, color: '#0f172a' }}>{item.role}</span>
                              {isNewOrUpdated && (
                                <span
                                  style={{
                                    background: '#dcfce7',
                                    color: '#166534',
                                    fontSize: '9.5px',
                                    fontWeight: 700,
                                    padding: '1px 5px',
                                    borderRadius: '3px',
                                  }}
                                >
                                  Updated / New
                                </span>
                              )}
                            </div>
                            {leadSummary && (
                              <div style={{ fontSize: '11px', color: '#475569', marginTop: '2px' }}>
                                <i
                                  className="fas fa-user"
                                  style={{ marginRight: '4px', fontSize: '9px', color: '#94a3b8' }}
                                />
                                {leadSummary}
                              </div>
                            )}
                          </div>
                        );
                      })
                    ) : (
                      <div style={{ fontSize: '11.5px', color: '#334155' }}>
                        {rolesToTarget.join(', ')}
                      </div>
                    )}
                  </div>
                </div>

                {/* Column 2: Tab 3 Current Matrix */}
                <div
                  style={{
                    background: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    padding: '10px 12px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      paddingBottom: '5px',
                      borderBottom: '1px solid #f1f5f9',
                    }}
                  >
                    <span
                      style={{
                        fontSize: '11px',
                        fontWeight: 700,
                        color: '#0f172a',
                        textTransform: 'uppercase',
                        letterSpacing: '0.4px',
                      }}
                    >
                      <i
                        className="fas fa-layer-group"
                        style={{ marginRight: '5px', color: '#64748b' }}
                      />
                      Tab 3: Current Matrix
                    </span>
                    <span style={{ fontSize: '11px', color: '#64748b' }}>
                      {currentMatrixRoles.length} persona{currentMatrixRoles.length > 1 ? 's' : ''}
                    </span>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    {currentMatrixRoles.length > 0 ? (
                      currentMatrixRoles.map((personaName, idx) => {
                        const isOutdated = !tab2RolesLowerSet.has(personaName.toLowerCase());
                        return (
                          <div
                            key={idx}
                            style={{
                              background: isOutdated ? '#fef2f2' : '#f8fafc',
                              border: `1px solid ${isOutdated ? '#fecaca' : '#e2e8f0'}`,
                              borderRadius: '6px',
                              padding: '6px 8px',
                              fontSize: '11.5px',
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: '4px',
                              }}
                            >
                              <span
                                style={{
                                  fontWeight: 600,
                                  color: isOutdated ? '#991b1b' : '#334155',
                                }}
                              >
                                {personaName}
                              </span>
                              {isOutdated && (
                                <span
                                  style={{
                                    background: '#fee2e2',
                                    color: '#991b1b',
                                    fontSize: '9.5px',
                                    fontWeight: 700,
                                    padding: '1px 5px',
                                    borderRadius: '3px',
                                  }}
                                >
                                  Outdated
                                </span>
                              )}
                            </div>
                            {isOutdated && (
                              <div
                                style={{ fontSize: '10.5px', color: '#b91c1c', marginTop: '2px' }}
                              >
                                No matching lead in Tab 2
                              </div>
                            )}
                          </div>
                        );
                      })
                    ) : (
                      <div style={{ fontSize: '11.5px', color: '#94a3b8', fontStyle: 'italic' }}>
                        Empty / None
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Informative footer note */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '11.5px',
                  color: '#475569',
                }}
              >
                <i className="fas fa-info-circle" style={{ color: '#2563eb' }} />
                <span>
                  Syncing will update <strong>Messaging (Tab 3)</strong>,{' '}
                  <strong>Cadence (Tab 4)</strong>, and <strong>Forecasts (Tab 5)</strong> for the
                  new roles.
                </span>
              </div>
            </>
          ) : (
            <>
              {/* Standard Step 2 -> Step 3 Transition Mode */}
              <div
                style={{
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  padding: '12px 14px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px',
                  fontSize: '12.5px',
                }}
              >
                <div
                  style={{
                    fontWeight: 600,
                    color: '#0f172a',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  <i className="fas fa-users" style={{ color: '#2563eb' }} />
                  <span>
                    {hasImportedLeads
                      ? `Target Roles from Imported Leads (${importedLeads.length} leads)`
                      : `Target Personas (${rolesToTarget.length} roles)`}
                  </span>
                </div>
                {hasImportedLeads && importedLeadRoles.length > 0 ? (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                      marginTop: '4px',
                    }}
                  >
                    {importedLeadRoles.map((item, idx) => {
                      const leadNames = item.leads.map((l) => {
                        const name =
                          l.name ||
                          `${l.firstName || ''} ${l.lastName || ''}`.trim() ||
                          l.email ||
                          'Lead';
                        const companyStr = l.company ? ` (${l.company})` : '';
                        return `${name}${companyStr}`;
                      });
                      const leadSummary =
                        leadNames.length <= 2
                          ? leadNames.join(', ')
                          : `${leadNames.slice(0, 2).join(', ')} +${leadNames.length - 2} more`;
                      return (
                        <div key={idx} style={{ fontSize: '12px', color: '#334155' }}>
                          <strong>{item.role}</strong>:{' '}
                          <span style={{ color: '#64748b' }}>{leadSummary}</span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ color: '#475569', fontSize: '12px' }}>
                    {rolesToTarget.join(' • ')}
                  </div>
                )}
              </div>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '11.5px',
                  color: '#475569',
                }}
              >
                <i className="fas fa-magic" style={{ color: '#4f46e5' }} />
                <span>
                  AI will generate tailored <strong>Messaging Matrix (Tab 3)</strong>,{' '}
                  <strong>Cadence Journey (Tab 4)</strong>, and{' '}
                  <strong>Forecast Simulation (Tab 5)</strong>.
                </span>
              </div>
            </>
          )}
        </div>

        {/* Modal Footer */}
        <div className="cmp-modal-footer">
          <div className="cmp-modal-footer-left">
            <button type="button" className="cmp-btn-secondary" onClick={onClose}>
              Cancel
            </button>
          </div>
          <div className="cmp-modal-footer-right">
            <button
              type="button"
              className="cmp-btn-secondary"
              onClick={onConfirmBlankForm}
              title="Keep current values and add blank cards for new roles"
            >
              Skip for now
            </button>
            <button
              type="button"
              className="cmp-btn-primary"
              style={{ minWidth: '160px', justifyContent: 'center' }}
              onClick={onConfirmAiGenerate}
            >
              <i className={isRoleMismatch ? 'fas fa-sync-alt' : 'fas fa-magic'} />{' '}
              {isRoleMismatch ? 'Sync Strategy' : 'Generate with AI'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
